import * as Yup from "yup"

/**
 * Meta Ad Creative Character Limits
 *
 * These limits are based on Meta's official Ads Guide:
 * https://www.facebook.com/business/ads-guide/update
 *
 * Note: Meta has two types of limits:
 * - RECOMMENDED: Characters that are fully visible without truncation
 * - MAX: Hard limits enforced by the API
 */
export const META_CREATIVE_LIMITS = {
  /**
   * Headline limits
   * - RECOMMENDED (27): Full visibility on Facebook Feed
   * - MAX (40): API hard limit
   */
  HEADLINE_RECOMMENDED: 27,
  HEADLINE_MAX: 40,

  /**
   * Description limits
   * - RECOMMENDED (125): Visible on most placements
   * - MAX (255): Extended limit for some placements
   */
  DESCRIPTION_RECOMMENDED: 125,
  DESCRIPTION_MAX: 255,

  /**
   * Primary Text limits
   * - RECOMMENDED (125): Visible before "See More" on most placements
   * - EXTENDED (150): Extended visibility on some placements
   * - MAX (2200): API hard limit (Facebook's maximum post length)
   */
  PRIMARY_TEXT_RECOMMENDED: 125,
  PRIMARY_TEXT_EXTENDED: 150,
  PRIMARY_TEXT_MAX: 2200,

  /**
   * URL limits
   * - MAX (2048): Standard URL length limit
   */
  URL_MAX: 2048,
} as const

/**
 * Validation schema for Ad Forge filters
 * Uses Yup for comprehensive form validation instead of manual sanitization
 */

export interface AdForgeFilters {
  campaignFilter: string
  statusFilter: string
  minSpend: string | number
  maxSpend: string | number
  searchQuery: string
}

export const adForgeFiltersSchema = Yup.object().shape({
  campaignFilter: Yup.string()
    .matches(
      /^[a-zA-Z0-9]*$/,
      "Campaign ID can only contain letters and numbers",
    )
    .max(100, "Campaign ID is too long")
    .optional(),

  statusFilter: Yup.string()
    .oneOf(["ALL", "ACTIVE", "PAUSED", "DELETED", "ARCHIVED"], "Invalid status")
    .default("ACTIVE"),

  minSpend: Yup.mixed()
    .test(
      "min-spend-validation",
      "Minimum spend must be a valid positive number",
      value => {
        if (!value && value !== 0) return true // Empty is allowed
        const stringValue = String(value)
        if (!/^\d*\.?\d*$/.test(stringValue)) return false
        const num = parseFloat(stringValue)
        return !isNaN(num) && num >= 0 && num <= 1000000000
      },
    )
    .optional(),

  maxSpend: Yup.mixed()
    .test(
      "max-spend-validation",
      "Maximum spend must be a valid positive number",
      value => {
        if (!value && value !== 0) return true // Empty is allowed
        const stringValue = String(value)
        if (!/^\d*\.?\d*$/.test(stringValue)) return false
        const num = parseFloat(stringValue)
        return !isNaN(num) && num >= 0 && num <= 1000000000
      },
    )
    .test(
      "max-greater-than-min",
      "Maximum spend must be greater than minimum spend",
      function (value) {
        const { minSpend } = this.parent
        if ((!value && value !== 0) || (!minSpend && minSpend !== 0)) {
          return true
        }
        const maxNum = parseFloat(String(value))
        const minNum = parseFloat(String(minSpend))
        return maxNum >= minNum
      },
    )
    .optional(),

  searchQuery: Yup.string()
    .matches(
      /^[a-zA-Z0-9\s\-_.,()]*$/,
      "Search query contains invalid characters",
    )
    .max(200, "Search query is too long")
    .optional(),
})

export const initialFiltersValues: AdForgeFilters = {
  campaignFilter: "",
  statusFilter: "ALL",
  minSpend: "",
  maxSpend: "",
  searchQuery: "",
}

/**
 * Validation schema for blast operation
 */
export interface BlastFormData {
  targetAdSets: Record<string, string[]>
  sourceAdId: string
  sourcePlatform: string
}

export const blastFormSchema = Yup.object().shape({
  targetAdSets: Yup.object()
    .test(
      "has-selections",
      "Please select at least one ad set to blast to",
      value => {
        if (!value) return false
        return Object.values(value).some(
          adSets => Array.isArray(adSets) && adSets.length > 0,
        )
      },
    )
    .test("valid-adset-ids", "Invalid ad set IDs", value => {
      if (!value) return true
      return Object.values(value).every(
        adSets =>
          Array.isArray(adSets) &&
          adSets.every(
            (id: string) =>
              typeof id === "string" &&
              /^[a-zA-Z0-9]+$/.test(id) &&
              id.length <= 100,
          ),
      )
    }),

  sourceAdId: Yup.string()
    .required("Source ad ID is required")
    .matches(/^[a-zA-Z0-9]+$/, "Invalid ad ID format")
    .max(100, "Ad ID is too long"),

  sourcePlatform: Yup.string()
    .required("Source platform is required")
    .oneOf(
      ["meta", "tiktok", "google", "twitter", "linkedin"],
      "Invalid platform",
    ),
})

/**
 * Helper function to sanitize form values after validation
 * This ensures clean data even after Yup validation
 */
export function sanitizeValidatedFilters(
  values: AdForgeFilters,
): AdForgeFilters {
  return {
    campaignFilter:
      typeof values.campaignFilter === "string"
        ? values.campaignFilter.trim()
        : String(values.campaignFilter || ""),
    statusFilter: values.statusFilter || "ACTIVE",
    minSpend:
      typeof values.minSpend === "string"
        ? values.minSpend.trim()
        : String(values.minSpend || ""),
    maxSpend:
      typeof values.maxSpend === "string"
        ? values.maxSpend.trim()
        : String(values.maxSpend || ""),
    searchQuery:
      typeof values.searchQuery === "string"
        ? values.searchQuery.trim()
        : String(values.searchQuery || ""),
  }
}

/**
 * Helper function to validate and format spend values
 */
export function formatSpendValue(value: string): string {
  if (!value) return ""
  const num = parseFloat(value)
  if (isNaN(num)) return ""
  return (Math.round(num * 100) / 100).toString() // Round to 2 decimal places
}

/**
 * Creative overrides interface and validation schema
 * Used for cross-objective ad blasting
 */
export interface CreativeOverridesFormData {
  destinationUrl?: string
  headline?: string
  description?: string
  primaryText?: string
  callToAction?: string
}

// Valid CTA values for Meta platform
const VALID_CTA_VALUES = [
  "APPLY_NOW",
  "BOOK_NOW",
  "CONTACT_US",
  "DOWNLOAD",
  "GET_OFFER",
  "GET_QUOTE",
  "INSTALL_NOW",
  "LEARN_MORE",
  "LISTEN_NOW",
  "PLAY_GAME",
  "SEE_MORE",
  "SHOP_NOW",
  "SIGN_UP",
  "SUBSCRIBE",
  "USE_APP",
  "WATCH_MORE",
  "WATCH_NOW",
  "LIKE_PAGE",
]

export const creativeOverridesSchema = Yup.object().shape({
  destinationUrl: Yup.string()
    .url("Must be a valid URL")
    .max(
      META_CREATIVE_LIMITS.URL_MAX,
      `URL must be ${META_CREATIVE_LIMITS.URL_MAX} characters or less`,
    )
    .optional(),

  headline: Yup.string()
    .max(
      META_CREATIVE_LIMITS.HEADLINE_MAX,
      `Headline must be ${META_CREATIVE_LIMITS.HEADLINE_MAX} characters or less (${META_CREATIVE_LIMITS.HEADLINE_RECOMMENDED} recommended for full visibility)`,
    )
    .matches(/^[^<>]*$/, "Headline cannot contain HTML tags")
    .optional(),

  description: Yup.string()
    .max(
      META_CREATIVE_LIMITS.DESCRIPTION_MAX,
      `Description must be ${META_CREATIVE_LIMITS.DESCRIPTION_MAX} characters or less (${META_CREATIVE_LIMITS.DESCRIPTION_RECOMMENDED} recommended)`,
    )
    .matches(/^[^<>]*$/, "Description cannot contain HTML tags")
    .optional(),

  primaryText: Yup.string()
    .max(
      META_CREATIVE_LIMITS.PRIMARY_TEXT_MAX,
      `Primary text must be ${META_CREATIVE_LIMITS.PRIMARY_TEXT_MAX} characters or less`,
    )
    .optional(),

  callToAction: Yup.string()
    .oneOf(VALID_CTA_VALUES, "Invalid call to action")
    .optional(),
})

export const initialCreativeOverridesValues: CreativeOverridesFormData = {
  destinationUrl: undefined,
  headline: undefined,
  description: undefined,
  primaryText: undefined,
  callToAction: undefined,
}

/**
 * Extended blast form data with creative overrides
 */
export interface ExtendedBlastFormData extends BlastFormData {
  overrides?: CreativeOverridesFormData
  sourcePreference?: "object_story_id" | "creative_id" | "auto"
}

export const extendedBlastFormSchema = blastFormSchema.shape({
  overrides: creativeOverridesSchema.optional(),
  sourcePreference: Yup.string()
    .oneOf(
      ["object_story_id", "creative_id", "auto"],
      "Invalid source preference",
    )
    .optional(),
})
